import { t } from 'i18next';
import { Infinity as InfinityIcon } from 'lucide-react';
import { Trans } from 'react-i18next';

import { Progress } from '@/components/ui/progress-circle';
import { TenantPolicy, Usage } from '@activepieces/shared';

type ActiveFlowsUsageProps = {
  usage: Usage;
  policy: TenantPolicy;
};

export const ActiveFlowsUsage = ({ usage, policy }: ActiveFlowsUsageProps) => {
  const isUnlimited = policy.maxActiveFlows === null;
  const percentage = Math.min(
    100,
    (usage.activeFlows / (policy.maxActiveFlows ?? 1)) * 100,
  );

  return (
    <div className="flex flex-col gap-1">
      <div className="flex justify-between text-xs">
        <span className="font-medium uppercase text-muted-foreground">
          {t('Active flows')}
        </span>
        <div className="flex items-center gap-[2px] whitespace-nowrap">
          <span className="text-sm font-semibold">{usage.activeFlows}</span>
          <span className="text-muted-foreground/80">/</span>
          {isUnlimited ? (
            <InfinityIcon className="h-4 w-4 text-muted-foreground/80" />
          ) : (
            <span className="text-muted-foreground/80">
              {policy.maxActiveFlows}
            </span>
          )}
        </div>
      </div>
      <Progress
        value={isUnlimited ? 100 : percentage}
        className="h-1"
        indicatorClassName={
          isUnlimited ? 'bg-gradient-to-r from-primary to-white' : undefined
        }
      />
      {policy.maxActiveFlows !== null && (
        <span className="text-xs text-muted-foreground">
          {percentage >= 100 ? (
            t('Active flows limit reached')
          ) : (
            <Trans
              i18nKey="{count, plural, one {<strong>{count} more</strong> flow available} other {<strong>{count} more</strong> flows available}}"
              values={{
                count: (policy.maxActiveFlows ?? 0) - usage.activeFlows,
              }}
            />
          )}
        </span>
      )}
    </div>
  );
};
